// local hook used in the context of Risk Assessment
import { useCallback } from 'react';
import { useAtomValue } from 'jotai';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { format } from 'date-fns';
import ExcelJS from 'exceljs';
import { useGetAssembliesByRiskAssessmentFiltersLazyQuery } from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  riskAssessmentAssemblyTemplate,
  selectedStructureRiskAssessment,
} from '@/components/Analysis/state';

const TASK_FIELDS_TO_EXPAND: Array<{ key: string; label: string }> = [
  { key: 'risk_task_description', label: 'Task Description' },
  { key: 'risk_task_frequency', label: 'Task Frequency' },
  { key: 'risk_task_responsibility', label: 'Task Responsibility' },
];

const TASK_EXPAND_KEYS = new Set(TASK_FIELDS_TO_EXPAND.map((f) => f.key));

const parseTaskItems = (value: string): [string, string, string, string] => {
  const items = value
    .split('\n')
    .map((line) => line.replace(/^\d+\)\s*/, '').trim())
    .filter((line) => line.length > 0);
  return [items[0] ?? '', items[1] ?? '', items[2] ?? '', items[3] ?? ''];
};

export const useGenerateDataForXlsx = () => {
  const structureId = useGetStructureId();
  const template = useAtomValue(riskAssessmentAssemblyTemplate);
  const structure = useAtomValue(selectedStructureRiskAssessment);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [fetchAssembliesForXlsx, { loading: xlsxLoading }] =
    useGetAssembliesByRiskAssessmentFiltersLazyQuery();

  const generateDataForCsv = useCallback(async () => {
    const assembliesForXlsxData = await fetchAssembliesForXlsx({
      variables: {
        input: {
          structureId,
          templateId: template?.id!,
          includeComments: true,
        },
      },
    });
    const assembliesForXlsx = assembliesForXlsxData.data?.getAssembliesByRiskAssessmentFilters;

    if (!assembliesForXlsx) throw new Error('No data found');

    const data =
      template?.fields
        .filter((field) => field.isExportable)
        .flatMap(({ key, name }) => {
          if (TASK_EXPAND_KEYS.has(key!)) {
            const baseLabel =
              TASK_FIELDS_TO_EXPAND.find((f) => f.key === key)?.label ?? name ?? key ?? 'No Label';
            return [1, 2, 3, 4].map((itemNumber) => ({
              label: `${baseLabel} ${itemNumber}`,
              data: assembliesForXlsx?.map(({ templateFieldData }) => {
                const raw = templateFieldData[key!] || '';
                return parseTaskItems(raw)[itemNumber - 1] || 'N/A';
              }),
            }));
          }
          return [
            {
              label: name ?? key ?? 'No Label',
              data: assembliesForXlsx?.map(
                ({ templateFieldData }) => templateFieldData[key!] || 'N/A'
              ),
            },
          ];
        }) ?? [];
    return data;
  }, [fetchAssembliesForXlsx, structureId, template?.fields, template?.id]);

  const downloadXlsxFile = useCallback(async () => {
    try {
      const data = await generateDataForCsv();

      if (!data) throw new Error('No data found');

      const cleanedData = data.map((col) => ({
        ...col,
        data: col.data.map((value) => {
          if (typeof value === 'string') {
            let cleaned = value.replace(/""/g, '"');
            cleaned = cleaned.replace(/^"(.*)"$/, '$1');
            return cleaned;
          }
          return value;
        }),
      }));

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Equipment Strategy');

      const headerRow = worksheet.addRow(cleanedData.map((col) => col.label));

      headerRow.eachCell((cell) => {
        cell.font = { bold: true, name: 'Arial' };
        cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
      });

      for (let rowIndex = 0; rowIndex < cleanedData[0].data.length; rowIndex++) {
        const rowData = cleanedData.map((col) => col.data[rowIndex]);
        const row = worksheet.addRow(rowData);

        row.eachCell((cell) => {
          cell.font = { name: 'Arial' };
          cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
        });

        row.height = 20;
      }

      cleanedData.forEach((col, index) => {
        const headerWidth = (col.label?.toString() || '').length;
        const maxDataWidth = Math.max(...col.data.map((cell) => (cell?.toString() || '').length));
        const columnWidth = Math.min(Math.max(headerWidth, maxDataWidth, 10) + 2, 50);

        const column = worksheet.getColumn(index + 1);
        column.width = columnWidth;
      });

      const date = format(new Date(), 'dd-MMM-yyyy');
      const fileName = `equipment-strategy-${structure?.name}-${date}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();

      window.URL.revokeObjectURL(url);

      showSnackBar('The ES Sheet has been exported successfully', 'success');
    } catch (error) {
      console.error('Export error:', error);
      showSnackBar('An error occurred while exporting the ES Sheet', 'error');
    }
  }, [generateDataForCsv, showSnackBar, structure?.name]);

  return { downloadXlsxFile, xlsxLoading };
};
